import { useMutation, useQuery } from '@tanstack/react-query';

import {
  DEPARTING_ARR_AIRPORT,
  DEPARTING_DEP_AIRPORT,
  RETURNING_ARR_AIRPORT,
  RETURNING_DEP_AIRPORT,
  buildLuxPlusPreviewPayloadFromRows,
  buildPreviewPayloadFromRows,
  generateCsvDataFromPreview,
  mapApiResultToRow,
} from '~/components/Flights/Charter/CharterFlightsPricingHelpers';
import type { CharterPricingRow } from '~/components/Flights/Charter/CharterFlightsPricingTypes';

import {
  type FareFamilyPricingParams,
  fetchAllCharterFareFamilyPricing,
  fetchCharterFareFamilyPricing,
  previewCharterFareFamilyPricing,
} from '~/services/CharterService';

/** Query keys for charter fare-family pricing — keyed by full param set so cache hits exact match. */
export const charterFareFamilyPricingQueryKeys = {
  all: ['charter-fare-family-pricing'] as const,
  list: (params: FareFamilyPricingParams) => [...charterFareFamilyPricingQueryKeys.all, params] as const,
};

/**
 * Fetches charter fare-family pricing for the given route.
 *
 * Pass `null` to disable the query (e.g. before search is triggered).
 *
 * Using TanStack Query here gives us:
 *  - Automatic deduplication / cancellation via `signal` (no race on rapid
 *    Search clicks — late-arriving responses don't overwrite the latest).
 *  - Built-in `isFetching` / `error` states.
 *  - Caching: re-selecting the same route avoids a network call.
 */
export function useGetCharterFareFamilyPricing(params: FareFamilyPricingParams | null) {
  return useQuery({
    queryKey: params
      ? charterFareFamilyPricingQueryKeys.list(params)
      : [...charterFareFamilyPricingQueryKeys.all, 'disabled'],
    queryFn: async ({ signal }) => {
      if (!params) {
        return [];
      }
      const response = await fetchCharterFareFamilyPricing(params, { signal });
      return response.result;
    },
    enabled: Boolean(params),
  });
}

/** Export every departure so seven-day return pairs survive table date/direction filters. */
export function useExportCharterFareFamilyPricing() {
  return useMutation({
    mutationFn: async (editedRows: Map<string, CharterPricingRow>) => {
      const results = await fetchAllCharterFareFamilyPricing({
        departingDepAirport: DEPARTING_DEP_AIRPORT,
        departingArrAirport: DEPARTING_ARR_AIRPORT,
        returningDepAirport: RETURNING_DEP_AIRPORT,
        returningArrAirport: RETURNING_ARR_AIRPORT,
      });
      const rows = results.map((item) => {
        const row = mapApiResultToRow(item);
        return editedRows.get(row.flightInstanceId) ?? row;
      });
      if (!rows.length) throw new Error('No pricing data found');
      const [standard, luxPlus] = await Promise.all([
        previewCharterFareFamilyPricing(buildPreviewPayloadFromRows(rows)),
        previewCharterFareFamilyPricing(buildLuxPlusPreviewPayloadFromRows(rows)),
      ]);
      if (!standard.result.length) throw new Error('No valid departure/return pairs found for CSV export');
      return generateCsvDataFromPreview(standard.result, luxPlus.result);
    },
  });
}
